import React, { createContext, useContext, useMemo } from "react";
import { useDetail } from "seed/gql";
import { useGet } from "seed/api";

const UserPermissionsContext = createContext(undefined);

export const useUserPermissions = (params) => {
  const context = useContext(UserPermissionsContext);
  if (!context)
    throw new Error("useUserPermissions must be used within a UserPermissionsProvider");

  return context.usePermissions(params);
};

export const UserPermissionsProvider = ({ children }) => {

  const usePermissions = ({ userId, flowId, collectionId = null, nodeId = null }) => {

    const reqPermissions = useGet("/users/get_perms", { flow_id: flowId });

    const reqFlow = useDetail(`{
      flow {
        chatSettings
        user {
          accountSettings
        }
      }
    }`, flowId);

    const reqCollection = useDetail(`{
      collection {
        settings
      }
    }`, collectionId);

    const reqNode = useDetail(`{
      flowNode {
        rootSettings
      }
    }`, nodeId);

    const userPermissions = reqPermissions.data || {};
    const role = userPermissions?.role;
    const isAdmin = userPermissions?.is_admin;

    const flow = reqFlow.data?.flow || {};
    const flowChatSettings = flow?.chatSettings || {};
    const flowChatPermissions = flowChatSettings?.permissions || {};

    const collection = reqCollection.data?.collection || {};
    const collectionSettings = collection?.settings || {};
    const collectionPermissions = collectionSettings?.permissions || {};

    const node = reqNode.data?.flowNode || {};
    const nodeRootSettings = node?.rootSettings || {};
    const nodePermissions = nodeRootSettings?.permissions || {};

    const perms = useMemo(() => {

      const permissions = {
        is_admin: isAdmin,
        role,
        flow: {},
        collection: {},
        node: {}
      };

      Object.keys(flowChatPermissions).forEach(key => {
        permissions.flow[key] = isAdmin || flowChatPermissions[key][role?.id] || false;
      });

      Object.keys(collectionPermissions).forEach(key => {
        permissions.collection[key] = isAdmin || collectionPermissions[key][role?.id] || false;
      });

      Object.keys(nodePermissions).forEach(key => {
        permissions.node[key] = isAdmin || nodePermissions[key][role?.id] || false;
      });

      return permissions;

    }, [isAdmin, role, flowChatPermissions, collectionPermissions, nodePermissions]);
    return perms;

  };

  return (
    <UserPermissionsContext.Provider value={{ usePermissions }}>
      {children}
    </UserPermissionsContext.Provider>
  );
};